import { useState, useRef, useEffect, useMemo, KeyboardEvent } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import {
  Send, FileEdit, Sparkles, Loader2, ChevronDown, Wand2,
  Settings2, Plus, Trash2, Save,
} from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
  DropdownMenuLabel,
  DropdownMenuSeparator,
} from "@/components/ui/dropdown-menu";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle,
} from "@/components/ui/dialog";
import { inboxApi, aiApi, snippetsApi, type InboxSnippet } from "@/lib/api";
import { cn } from "@/lib/utils";
import type { InboxMessage } from "@/types/api";

// Defaults shown only when the user has zero snippets yet — prefilled in
// the "Manage snippets" dialog as starter templates they can edit/delete.
const DEFAULT_SNIPPETS: Array<{ name: string; body: string }> = [
  {
    name: "📅 Book a call",
    body: "Thanks for the reply — happy to walk you through this.\n\nGrab any 30 min that works on my calendar: https://luzgridtech.com/book\n\nIf you'd prefer a specific time, just send me 2-3 windows and I'll confirm.\n\n— Jose",
  },
  {
    name: "📎 Send proposal",
    body: "Great — I'll put together a proposal sized to your environment and send it your way by EOD tomorrow.\n\nQuick check before I do: rough headcount, are you on M365 or Google, and is there a current MSP we'd be transitioning from?\n\n— Jose",
  },
  {
    name: "💬 Discovery questions",
    body: "Thanks. Before we hop on a call, three quick questions to keep that time efficient:\n\n1. How many employees / endpoints are we covering?\n2. What's prompting the look right now (incident, pricing, growth)?\n3. Any compliance lift we'd need to support (HIPAA, SOC 2, GLBA)?\n\nReply right here works fine.\n\n— Jose",
  },
  {
    name: "✋ Not a fit (polite no)",
    body: "Appreciate you taking the time to reply.\n\nBased on what you described it doesn't sound like a clean fit on our side right now. I'll close the loop here, but feel free to reach back out if anything changes.\n\n— Jose",
  },
  {
    name: "🔁 Circle back later",
    body: "Got it — totally understand the timing.\n\nMind if I check back in a quarter? I'll add a note and ping you then.\n\n— Jose",
  },
];

interface ComposerProps {
  conversationId: string;
  /** Latest message in the thread — we reply to its graphMessageId so the
   *  worker uses Graph's /reply endpoint and threading is preserved. */
  latestMessage: InboxMessage | null;
}

export default function Composer({ conversationId, latestMessage }: ComposerProps) {
  const [body, setBody] = useState("");
  const [manageOpen, setManageOpen] = useState(false);
  // Recipients default to the original sender for an inbound thread, or to
  // the original recipients if the latest message we have is outbound. The
  // user can edit before sending.
  //
  // The trick: latestMessage is null on first render (react-query hasn't
  // resolved yet) and arrives on the next render. If we initialize a state
  // var from latestMessage in useState, the initial empty value sticks
  // forever. Instead we keep an OPTIONAL override (null = use derived
  // default) and reset it to null when the thread changes. The visible
  // value is `effectiveToAddrs`.
  const [toAddrsOverride, setToAddrsOverride] = useState<string | null>(null);
  const ref = useRef<HTMLTextAreaElement>(null);
  const qc = useQueryClient();

  const defaultTo = useMemo(() => {
    if (!latestMessage) return "";
    return latestMessage.direction === "outbound"
      ? (latestMessage.toAddresses ?? []).join(", ")
      : (latestMessage.fromAddress ?? "");
  }, [latestMessage?.direction, latestMessage?.fromAddress, latestMessage?.toAddresses]);

  const toAddrs = toAddrsOverride ?? defaultTo;

  // When the active thread changes, drop the override + clear body so we
  // don't carry a half-typed reply over to a different conversation.
  useEffect(() => {
    setToAddrsOverride(null);
    setBody("");
  }, [conversationId]);

  const snippets = useQuery({
    queryKey: ["inbox", "snippets"],
    queryFn: () => snippetsApi.list(),
  });
  const liveSnippets = (snippets.data?.snippets ?? []).filter((s) => s.is_active);

  const splitAddrs = (s: string): string[] =>
    s.split(/[,;\s]+/).map((x) => x.trim()).filter(Boolean);

  const replyMutation = useMutation({
    mutationFn: () =>
      inboxApi.reply({
        messageId: latestMessage?.graphMessageId,
        to: splitAddrs(toAddrs),
        subject: latestMessage?.subject ? `Re: ${latestMessage.subject}` : undefined,
        body,
      }),
    onSuccess: () => {
      setBody("");
      // Refresh thread + list so the new message appears. Force a fresh
      // mirror sync on the next tick by also invalidating the body cache —
      // Graph's /reply lands the message but our mirror needs to pick it up.
      qc.invalidateQueries({ queryKey: ["inbox", "thread", conversationId] });
      qc.invalidateQueries({ queryKey: ["inbox", "threads"] });
    },
  });

  const draftMutation = useMutation({
    mutationFn: () =>
      inboxApi.draft({
        messageId: latestMessage?.graphMessageId,
        to: splitAddrs(toAddrs),
        subject: latestMessage?.subject ? `Re: ${latestMessage.subject}` : undefined,
        body,
      }),
    onSuccess: () => {
      setBody("");
      qc.invalidateQueries({ queryKey: ["inbox", "threads"] });
    },
  });

  const aiDraftMutation = useMutation({
    mutationFn: () => aiApi.draftReply(conversationId, body || undefined),
    onSuccess: (data) => {
      setBody(data.draft);
      setTimeout(() => ref.current?.focus(), 50);
    },
  });

  const onKeyDown = (e: KeyboardEvent<HTMLTextAreaElement>) => {
    // Cmd/Ctrl + Enter → Send
    if ((e.metaKey || e.ctrlKey) && e.key === "Enter") {
      e.preventDefault();
      if (body.trim()) replyMutation.mutate();
    }
    // Cmd/Ctrl + S → Save draft
    if ((e.metaKey || e.ctrlKey) && e.key.toLowerCase() === "s") {
      e.preventDefault();
      if (body.trim()) draftMutation.mutate();
    }
  };

  const insertSnippet = (snippet: string) => {
    setBody((prev) => (prev ? prev + "\n\n" + snippet : snippet));
    setTimeout(() => ref.current?.focus(), 50);
  };

  const sending = replyMutation.isPending;
  const drafting = draftMutation.isPending;
  const aiDrafting = aiDraftMutation.isPending;
  const error = replyMutation.error || draftMutation.error || aiDraftMutation.error;
  const empty = !body.trim();
  const replySent = replyMutation.isSuccess;
  const draftSaved = draftMutation.isSuccess;
  const noRecipients = splitAddrs(toAddrs).length === 0;

  return (
    <div className="border-t border-line bg-surface-panel">
      {error && (
        <div className="border-b border-danger/30 bg-danger/10 px-4 py-2 text-xs text-danger">
          Couldn't send: {(error as Error).message}
        </div>
      )}
      {replySent && (
        <div className="border-b border-success/30 bg-success/10 px-4 py-2 text-xs text-success">
          ✓ Reply sent — it'll show up here once the next mirror tick (≤5 min) syncs the Sent folder.
        </div>
      )}
      {draftSaved && (
        <div className="border-b border-brand/30 bg-brand/5 px-4 py-2 text-xs text-brand-deep">
          ✓ Draft saved to the shared Drafts folder.
        </div>
      )}

      <div className="flex items-center gap-2 px-4 pt-2.5 text-[11px] text-ink-dim">
        <span className="shrink-0">To</span>
        <input
          type="text"
          value={toAddrs}
          onChange={(e) => setToAddrsOverride(e.target.value)}
          placeholder="recipient@example.com"
          className="min-w-0 flex-1 border-0 bg-transparent text-[12px] text-ink outline-none placeholder:text-ink-dim focus:ring-0"
        />
        {latestMessage?.subject && (
          <span className="ml-auto shrink-0 truncate text-ink-muted">
            Re: {latestMessage.subject}
          </span>
        )}
      </div>

      <Textarea
        ref={ref}
        value={body}
        onChange={(e) => setBody(e.target.value)}
        onKeyDown={onKeyDown}
        placeholder="Type your reply… (⌘+Enter to send, ⌘+S to save draft)"
        rows={5}
        className="m-3 resize-none border-0 bg-transparent shadow-none focus:ring-0"
      />

      <div className="flex items-center gap-2 border-t border-line bg-surface-panel2/40 px-3 py-2">
        <Button
          size="sm"
          variant="ghost"
          className="gap-1.5 text-accent-purple hover:bg-accent-purple/10"
          disabled={aiDrafting}
          onClick={() => aiDraftMutation.mutate()}
          title="Draft a reply with AI"
        >
          {aiDrafting ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <Wand2 className="h-3.5 w-3.5" />}
          <span>AI draft</span>
        </Button>

        <DropdownMenu>
          <DropdownMenuTrigger asChild>
            <Button size="sm" variant="ghost" className="gap-1.5">
              <Sparkles className="h-3.5 w-3.5" />
              <span>Snippets</span>
              <ChevronDown className="h-3 w-3 opacity-60" />
            </Button>
          </DropdownMenuTrigger>
          <DropdownMenuContent align="start" className="w-72">
            <DropdownMenuLabel>Insert a snippet</DropdownMenuLabel>
            <DropdownMenuSeparator />
            {snippets.isLoading ? (
              <div className="px-2 py-2 text-xs text-ink-muted">Loading…</div>
            ) : liveSnippets.length === 0 ? (
              <div className="px-2 py-2 text-xs text-ink-muted">
                No snippets yet. Click "Manage snippets" below to add one.
              </div>
            ) : liveSnippets.map((s) => (
              <DropdownMenuItem
                key={s.id}
                onClick={() => insertSnippet(s.body)}
                className="flex-col items-start"
              >
                <span className="text-sm">{s.name}</span>
                <span className="line-clamp-1 text-[11px] text-ink-dim">
                  {s.body.split("\n")[0]}
                </span>
              </DropdownMenuItem>
            ))}
            <DropdownMenuSeparator />
            <DropdownMenuItem onClick={() => setManageOpen(true)} className="text-xs">
              <Settings2 className="mr-2 h-3 w-3" /> Manage snippets…
            </DropdownMenuItem>
          </DropdownMenuContent>
        </DropdownMenu>

        <SnippetManager
          open={manageOpen}
          onClose={() => setManageOpen(false)}
          snippets={snippets.data?.snippets ?? []}
        />

        <span
          className={cn(
            "ml-auto text-[11px]",
            empty ? "text-ink-dim" : "text-ink-muted",
          )}
        >
          {empty ? "Empty" : `${body.length} chars`}
        </span>

        <Button
          size="sm"
          variant="ghost"
          disabled={empty || drafting || sending}
          onClick={() => draftMutation.mutate()}
          title="⌘+S — save to shared Drafts folder"
          className="gap-1.5"
        >
          {drafting ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <FileEdit className="h-3.5 w-3.5" />}
          <span>Save draft</span>
        </Button>

        <Button
          size="sm"
          disabled={empty || noRecipients || sending || drafting}
          onClick={() => replyMutation.mutate()}
          title={noRecipients ? "Add at least one recipient" : "⌘+Enter — send via shared mailbox"}
          className="gap-1.5"
        >
          {sending ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <Send className="h-3.5 w-3.5" />}
          <span>Send</span>
        </Button>
      </div>
    </div>
  );
}

function SnippetManager({
  open, onClose, snippets,
}: { open: boolean; onClose: () => void; snippets: InboxSnippet[] }) {
  const qc = useQueryClient();
  const [draftName, setDraftName] = useState("");
  const [draftBody, setDraftBody] = useState("");

  const create = useMutation({
    mutationFn: (input: { name: string; body: string }) => snippetsApi.create(input.name, input.body),
    onSuccess: () => {
      setDraftName(""); setDraftBody("");
      qc.invalidateQueries({ queryKey: ["inbox", "snippets"] });
    },
  });
  const update = useMutation({
    mutationFn: (input: { id: string; patch: Partial<{ name: string; body: string; is_active: boolean }> }) =>
      snippetsApi.update(input.id, input.patch),
    onSuccess: () => qc.invalidateQueries({ queryKey: ["inbox", "snippets"] }),
  });
  const remove = useMutation({
    mutationFn: (id: string) => snippetsApi.delete(id),
    onSuccess: () => qc.invalidateQueries({ queryKey: ["inbox", "snippets"] }),
  });
  const seedDefaults = useMutation({
    mutationFn: async () => {
      // Seed all defaults sequentially. The caller already confirmed no
      // snippets exist; if some race-create them between check and seed we
      // accept the duplicates — user can delete.
      for (const d of DEFAULT_SNIPPETS) {
        await snippetsApi.create(d.name, d.body);
      }
    },
    onSuccess: () => qc.invalidateQueries({ queryKey: ["inbox", "snippets"] }),
  });

  return (
    <Dialog open={open} onOpenChange={(o) => !o && onClose()}>
      <DialogContent size="lg">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <Settings2 className="h-4 w-4" /> Manage snippets
          </DialogTitle>
        </DialogHeader>

        {snippets.length === 0 && (
          <div className="rounded border border-line bg-surface-panel2 p-3 text-xs text-ink-muted">
            <p>You don't have any snippets yet.</p>
            <Button
              size="sm"
              variant="outline"
              className="mt-2 gap-1.5"
              disabled={seedDefaults.isPending}
              onClick={() => seedDefaults.mutate()}
            >
              {seedDefaults.isPending && <Loader2 className="h-3.5 w-3.5 animate-spin" />}
              Seed default snippets
            </Button>
          </div>
        )}

        <div className="max-h-[40vh] space-y-2 overflow-y-auto pr-1 scrollbar-thin">
          {snippets.map((s) => (
            <SnippetRow
              key={s.id}
              snippet={s}
              onSave={(patch) => update.mutate({ id: s.id, patch })}
              onDelete={() => {
                if (confirm(`Delete snippet "${s.name}"?`)) remove.mutate(s.id);
              }}
              isSaving={update.isPending}
            />
          ))}
        </div>

        <div className="space-y-2 rounded border border-brand/30 bg-brand/5 p-3">
          <p className="text-[11px] font-semibold uppercase tracking-wider text-ink-dim">New snippet</p>
          <Input
            value={draftName}
            onChange={(e) => setDraftName(e.target.value)}
            placeholder="Name (e.g. 'Send proposal')"
          />
          <Textarea
            value={draftBody}
            onChange={(e) => setDraftBody(e.target.value)}
            placeholder="Body — markdown ok. Variables like {{firstName}} are supported."
            rows={4}
          />
          <div className="flex justify-end">
            <Button
              size="sm"
              disabled={!draftName.trim() || !draftBody.trim() || create.isPending}
              onClick={() => create.mutate({ name: draftName.trim(), body: draftBody.trim() })}
              className="gap-1.5"
            >
              {create.isPending ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <Plus className="h-3.5 w-3.5" />}
              Add snippet
            </Button>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
}

function SnippetRow({
  snippet, onSave, onDelete, isSaving,
}: {
  snippet: InboxSnippet;
  onSave: (patch: Partial<{ name: string; body: string; is_active: boolean }>) => void;
  onDelete: () => void;
  isSaving: boolean;
}) {
  const [name, setName] = useState(snippet.name);
  const [body, setBody] = useState(snippet.body);
  const dirty = name !== snippet.name || body !== snippet.body;

  return (
    <div className="space-y-1.5 rounded border border-line bg-surface-panel p-2.5">
      <div className="flex items-center gap-2">
        <Input value={name} onChange={(e) => setName(e.target.value)} className="h-8 flex-1" />
        <button
          onClick={() => onSave({ is_active: !snippet.is_active })}
          className={cn(
            "rounded border px-2 py-1 text-[11px] font-medium",
            snippet.is_active
              ? "border-success/40 bg-success/10 text-success"
              : "border-line bg-surface-panel2 text-ink-dim",
          )}
          title={snippet.is_active ? "Active — click to disable" : "Disabled — click to enable"}
        >
          {snippet.is_active ? "Active" : "Disabled"}
        </button>
        <button
          onClick={onDelete}
          className="rounded p-1 text-ink-dim hover:bg-surface-hover hover:text-danger"
          title="Delete"
        >
          <Trash2 className="h-3.5 w-3.5" />
        </button>
      </div>
      <Textarea value={body} onChange={(e) => setBody(e.target.value)} rows={3} className="text-xs" />
      <div className="flex items-center justify-between">
        <span className="text-[10px] text-ink-dim">Last edited {new Date(snippet.updated_at).toLocaleString()}</span>
        <Button
          size="sm"
          variant="ghost"
          disabled={!dirty || isSaving}
          onClick={() => onSave({ name, body })}
          className="gap-1.5"
        >
          {isSaving ? <Loader2 className="h-3 w-3 animate-spin" /> : <Save className="h-3 w-3" />}
          Save
        </Button>
      </div>
    </div>
  );
}
